import { useEffect, useState, useContext, useCallback, useMemo, useRef } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { SocketContext } from '../context/SocketContext';
import { useHideMenu } from '../hooks/useHideMenu';
import LoadingOverlay from '../components/LoadingOverlay';
import logoParaTicket from '../assets/femcc-01/logoParaTicket.png';
import logo from '../assets/logo.png';
import { serverApi } from '../api';
import { getUsuarioStorage } from '../helpers/getUsuarioStorage';
import { SelectBuscable } from '../components/SelectBuscable';
import { guardarColoresStorage } from '../helpers/theme';
import { parsearLecturaCedula, describirFormato } from '../helpers/lectorCedula';
import { useLectorCedula } from '../hooks/useLectorCedula';

/* ─────────────────────────────────────────────
   CONSTANTES
───────────────────────────────────────────── */
const tiposDocumentoLabels = {
    CC: 'Cédula de Ciudadanía',
    TI: 'Tarjeta de Identidad',
    RC: 'Registro Civil',
    CE: 'Cédula de Extranjería',
    PE: 'Permiso Especial de Permanencia',
    PT: 'Permiso por Protección Temporal',
};

const MySwal = withReactContent(Swal);

const sweet = (title, html) => {
    MySwal.fire({ title, html, showConfirmButton: false, timer: 10000 });
};

const iconSvgOk = `
  <svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="28" fill="#22C55E"/>
    <path d="M18 32l10 10 18-18" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

/* ── Utilidades de branding / iconos ─────────── */
const baseImageUrl = serverApi.defaults.baseURL
    ? serverApi.defaults.baseURL.replace(/\/api\/?$/, '')
    : '';

const fullImageUrl = (path) => {
    if (!path || typeof path !== 'string' || !path.trim()) return null;
    return path.trim().startsWith('http') ? path.trim() : `${baseImageUrl}${path.trim()}`;
};

const claseIcono = (item) => {
    const icono = (item?.icono || '').trim();
    if (!icono || icono === 'undefined') return 'fa-solid fa-ticket';
    return icono;
};

/* ─────────────────────────────────────────────
   SUB-COMPONENTE: LogoKiosko

   Muestra la imagen de marca con cadena de respaldos:
   1) recurso pedido (logo o ícono de la entidad)
   2) recurso alterno (ícono o logo)
   3) logo genérico incluido en la app
   Así un logo/ícono roto (404, archivo borrado) nunca deja el hueco vacío.
───────────────────────────────────────────── */
const LogoKiosko = ({ src, fallback, alt, style, className }) => {
    const candidatos = useMemo(
        () => [src, fallback, logo].filter((v, i, arr) => v && arr.indexOf(v) === i),
        [src, fallback]
    );

    const [idx, setIdx] = useState(0);

    // Al cambiar de entidad (o de recurso) se reinicia la cadena
    useEffect(() => { setIdx(0); }, [candidatos]);

    return (
        <img
            src={candidatos[Math.min(idx, candidatos.length - 1)] || logo}
            alt={alt}
            style={style}
            className={className}
            onError={() => setIdx(i => (i + 1 < candidatos.length ? i + 1 : i))}
        />
    );
};

/* ─────────────────────────────────────────────
   SUB-COMPONENTE: SelectorTipoDoc
───────────────────────────────────────────── */
const SelectorTipoDoc = ({ value, onChange }) => (
    <div className="ct2-selector-grid">
        {Object.entries(tiposDocumentoLabels).map(([key, label]) => {
            const active = value === key;
            return (
                <button
                    key={key}
                    type="button"
                    onClick={() => onChange(key)}
                    style={{
                        padding: '16px 8px',
                        borderRadius: 14,
                        border: active ? '3px solid var(--color-primario)' : '2px solid #dde1ea',
                        background: active
                            ? 'linear-gradient(135deg,var(--color-primario),var(--color-secundario))'
                            : '#f1f5f9',
                        color: active ? '#fff' : '#475569',
                        fontWeight: active ? 800 : 600,
                        fontSize: 14,
                        lineHeight: 1.2,
                        cursor: 'pointer',
                        transition: 'all 0.18s ease',
                        boxShadow: active
                            ? '0 4px 16px rgba(var(--color-primario-rgb),0.35)'
                            : '0 1px 3px rgba(0,0,0,0.08)',
                        textAlign: 'center',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        touchAction: 'manipulation',
                    }}
                >
                    <span style={{ fontSize: 16, fontWeight: 900 }}>{key}</span>
                    <br />
                    <span style={{ fontSize: 11, opacity: 0.85 }}>{label}</span>
                </button>
            );
        })}
    </div>
);

/* ─────────────────────────────────────────────
   SUB-COMPONENTE: TecladoNumerico
───────────────────────────────────────────── */
const NUMERIC_ROWS = [[1, 2, 3], [4, 5, 6], [7, 8, 9], [null, 0, 'DEL']];

const TecladoNumerico = ({ onKey }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {NUMERIC_ROWS.flat().map((key, idx) => {
            const isDel = key === 'DEL';
            const isEmpty = key === null;
            return (
                <button
                    key={idx}
                    type="button"
                    disabled={isEmpty}
                    onClick={() => !isEmpty && onKey(key)}
                    className="ct2-num-key"
                    style={{
                        borderRadius: 16,
                        border: 'none',
                        background: isEmpty
                            ? 'transparent'
                            : isDel
                                ? '#fee2e2'
                                : '#f8fafc',
                        color: isDel ? '#dc2626' : '#0f172a',
                        fontSize: isDel ? 22 : 28,
                        fontWeight: 700,
                        cursor: isEmpty ? 'default' : 'pointer',
                        boxShadow: isEmpty ? 'none' : '0 2px 8px rgba(0,0,0,0.08)',
                        transition: 'transform 0.1s, box-shadow 0.1s',
                        touchAction: 'manipulation',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    onTouchStart={e => {
                        if (!isEmpty) e.currentTarget.style.transform = 'scale(0.92)';
                    }}
                    onTouchEnd={e => {
                        e.currentTarget.style.transform = 'scale(1)';
                    }}
                >
                    {isDel ? '⌫' : key}
                </button>
            );
        })}
    </div>
);

/* ─────────────────────────────────────────────
   SUB-COMPONENTE: TecladoQwerty
───────────────────────────────────────────── */
const QWERTY_ROWS = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ñ'],
    ['⇧', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '⌫'],
    ['ESPACIO'],
];

const TecladoQwerty = ({ onKey, onDelete, onSpace, onCaps, capsOn }) => {
    const btnStyle = (key) => {
        const isSpecial = key === '⌫' || key === '⇧' || key === 'ESPACIO';
        const isCaps = key === '⇧';
        return {
            height: key === 'ESPACIO' ? 48 : 46,
            minWidth: key === 'ESPACIO' ? '50%' : 'auto',
            flex: key === 'ESPACIO' ? '0 0 50%' : '1 1 0',
            borderRadius: 10,
            border: 'none',
            background: isCaps && capsOn
                ? 'var(--color-primario)'
                : isSpecial
                    ? '#e2e8f0'
                    : '#f8fafc',
            color: isCaps && capsOn ? '#fff' : isSpecial ? '#475569' : '#0f172a',
            fontSize: key === 'ESPACIO' ? 14 : 18,
            fontWeight: isSpecial ? 700 : 600,
            cursor: 'pointer',
            boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
            transition: 'transform 0.1s',
            touchAction: 'manipulation',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        };
    };

    const handleKey = (key) => {
        if (key === '⌫') return onDelete();
        if (key === '⇧') return onCaps();
        if (key === 'ESPACIO') return onSpace();
        onKey(capsOn ? key : key.toLowerCase());
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {QWERTY_ROWS.map((row, ri) => (
                <div key={ri} style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: ri === 3 ? 'wrap' : 'nowrap' }}>
                    {row.map((key) => (
                        <button
                            key={key}
                            type="button"
                            style={btnStyle(key)}
                            onClick={() => handleKey(key)}
                            onTouchStart={e => { e.currentTarget.style.transform = 'scale(0.88)'; }}
                            onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                            {key === '⇧' ? (capsOn ? '⇑' : '⇧') : key === 'ESPACIO' ? 'ESPACIO' : key}
                        </button>
                    ))}
                </div>
            ))}
        </div>
    );
};

/* ─────────────────────────────────────────────
   SUB-COMPONENTE: CampoTextoTouch
───────────────────────────────────────────── */
const CampoTextoTouch = ({ label, value, active, onActivate }) => (
    <div
        style={{
            position: 'relative',
            marginBottom: 12,
            cursor: 'pointer',
        }}
        onClick={onActivate}
        onTouchEnd={onActivate}
    >
        <label style={{
            position: 'absolute', left: 14,
            top: value ? 7 : '50%',
            transform: value ? 'none' : 'translateY(-50%)',
            fontSize: value ? 11 : 15,
            fontWeight: value ? 700 : 400,
            color: active ? 'var(--color-primario)' : value ? 'var(--color-secundario)' : '#94a3b8',
            transition: 'all 0.2s ease',
            pointerEvents: 'none',
            letterSpacing: value ? 0.8 : 0,
            textTransform: value ? 'uppercase' : 'none',
            zIndex: 1,
        }}>
            {label}
        </label>
        <div style={{
            width: '100%',
            minHeight: 56,
            padding: value ? '24px 14px 8px' : '16px 14px',
            background: active ? '#e6f4f2' : '#f8fafc',
            border: `2px solid ${active ? 'var(--color-primario)' : '#e2e8f0'}`,
            borderRadius: 12,
            color: '#0f172a',
            fontSize: 18,
            fontWeight: 600,
            boxShadow: active ? '0 0 0 4px rgba(var(--color-primario-rgb),0.15)' : 'none',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box',
            userSelect: 'none',
            letterSpacing: 0.5,
        }}>
            {value || <span style={{ color: 'transparent' }}>_</span>}
            {active && (
                <span style={{
                    display: 'inline-block',
                    width: 2,
                    height: 20,
                    background: 'var(--color-primario)',
                    marginLeft: 2,
                    verticalAlign: 'middle',
                    animation: 'blink 1s step-end infinite',
                }} />
            )}
        </div>
    </div>
);

/* ─────────────────────────────────────────────
   SUB-COMPONENTE: CardTipoAtencion
───────────────────────────────────────────── */
const CardTipoAtencion = ({ item, onClick }) => {
    const [pressed, setPressed] = useState(false);
    const color = item.color || 'var(--color-primario)';

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onTouchStart={() => setPressed(true)}
            onTouchEnd={() => { setPressed(false); onClick(); }}
            onTouchCancel={() => setPressed(false)}
            style={{
                background: pressed ? '#e6f4f2' : 'white',
                border: `2px solid ${pressed ? 'var(--color-primario)' : '#e8edf5'}`,
                borderLeft: `6px solid ${color}`,
                borderRadius: 14,
                padding: '14px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                boxShadow: pressed
                    ? '0 2px 12px rgba(var(--color-primario-rgb),0.18)'
                    : '0 4px 12px rgba(0,0,0,0.05)',
                transform: pressed ? 'scale(0.98)' : 'scale(1)',
                transition: 'all 0.15s ease',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                touchAction: 'manipulation',
            }}
        >
            <div style={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                background: `${color}18`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}>
                <i className={claseIcono(item)} style={{ color, fontSize: 21 }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16.5, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                    {item.tipoAtencion}
                </div>
                {item.complemento && (
                    <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 3, fontWeight: 400 }}>
                        {item.complemento}
                    </div>
                )}
            </div>
            <div style={{ color: '#94a3b8', fontSize: 22, fontWeight: 'bold' }}>›</div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   COMPONENTE PRINCIPAL
───────────────────────────────────────────── */
export const CrearTicketV2 = () => {
    useHideMenu(true);

    const { conectarSocket } = useContext(SocketContext);

    /* ── Estados globales ── */
    const [usuario] = useState(getUsuarioStorage());
    const [entidadInfo, setEntidadInfo] = useState(null);
    const entidadStorage = usuario.codigoEntidad;

    const [isLoading, setIsLoading] = useState(false);
    const [tipoAtencion, setTipoAtencion] = useState([]);

    /* ── Datos de pantalla setup ── */
    const [setupForm, setSetupForm] = useState({
        codigoEntidad: 'femcc-01',
        codigoUbicacion: '',
    });
    const [ubicaciones, setUbicaciones] = useState([]);
    const [entidadSetup, setEntidadSetup] = useState(null);

    /* ── Flujo principal ── */
    const [paso, setPaso] = useState(1); // 1=documento | 1b=datos-nuevo | 2=tipo-atencion

    /* ── Paso 1: datos de documento ── */
    const [tipoDoc, setTipoDoc] = useState('CC');
    const [numDoc, setNumDoc] = useState('');

    /* ── Paso 1b: datos nuevo paciente ── */
    const [datosPaciente, setDatosPaciente] = useState({
        nombre1: '', nombre2: '', apellido1: '', apellido2: '',
    });
    const [campoActivo, setCampoActivo] = useState('nombre1');
    const [capsOn, setCapsOn] = useState(true);

    /* ── Lectura con pistola de código de barras (cédulas) ── */
    const [lecturaCedula, setLecturaCedula] = useState(null);

    /* ── Paso 2 ── */
    const [pacienteNombre, setPacienteNombre] = useState('');
    const [pacienteEsNuevo, setPacienteEsNuevo] = useState(false);
    const [prioridad, setPrioridad] = useState(false);

    /* ── Ancho de pantalla (para elegir logo o ícono, como en el navbar) ── */
    const [pantallaChica, setPantallaChica] = useState(window.innerWidth < 768);

    useEffect(() => {
        const onResize = () => setPantallaChica(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    /* ── Branding efectivo de la entidad ── */
    const branding = entidadInfo || usuario;
    const urlLogo = fullImageUrl(branding?.imagenLogo);
    const urlIcono = fullImageUrl(branding?.imagenIcono);

    /* ── Marca según el ancho de pantalla ──
       Mismo criterio que el navbar de administración (RouterPage.js): en
       pantallas grandes se muestra el logo horizontal y en las pequeñas el
       ícono (isotipo). El otro recurso queda de respaldo y, al final, el logo
       genérico incluido en la app (lo agrega LogoKiosko). */
    const logoMarca = pantallaChica ? (urlIcono || urlLogo) : (urlLogo || urlIcono);
    const logoMarcaRespaldo = pantallaChica ? (urlLogo || urlIcono) : (urlIcono || urlLogo);
    const logoPrint = urlLogo || urlIcono || logoParaTicket;

    /* ─── Carga tipos de atención al montar ─── */
    useEffect(() => {
        const fetchTipos = async () => {
            try {
                setIsLoading(true);
                const resp = await serverApi.post('/tipos-atencion/lista-para-ticket', {
                    codigoEntidad: localStorage.getItem('codigoEntidad'),
                    codigoUbicacion: localStorage.getItem('codigoUbicacion'),
                });
                if (resp.data.atenciones) setTipoAtencion(resp.data.atenciones);
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoading(false);
            }
        };
        if (entidadStorage) fetchTipos();
    }, [entidadStorage]);

    /* ─── Carga branding de la entidad (logo / nombre) ─── */
    useEffect(() => {
        const cargarEntidad = async () => {
            const cod = localStorage.getItem('codigoEntidad');
            if (!cod) return;
            try {
                const resp = await serverApi.post('/auth/select-ubicacion-pantallas', { codigoEntidad: cod });
                const ent = resp.data?.entidad;
                if (ent) {
                    localStorage.setItem('nombreEntidad', ent.nombreEntidad || '');
                    localStorage.setItem('imagenLogo', ent.imagenLogo || '');
                    localStorage.setItem('imagenIcono', ent.imagenIcono || '');
                    guardarColoresStorage(ent.colorPrimario, ent.colorSecundario);
                    setEntidadInfo(ent);
                }
            } catch (e) {
                console.error(e);
            }
        };
        cargarEntidad();
    }, [entidadStorage]);

    /* ─── Setup: buscar ubicaciones ─── */
    const fetchUbicaciones = useCallback(async (codigo) => {
        if (!codigo || codigo.trim().length < 3) return;
        try {
            const resp = await serverApi.post('/auth/select-ubicacion-pantallas', {
                codigoEntidad: codigo,
            });
            const data = (resp.data.ubicaciones || []).map(i => ({
                value: i.codigoUbicacion,
                label: i.nombreUbicacion,
            }));
            setUbicaciones(data);
            setEntidadSetup(resp.data?.entidad || null);
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        if (!entidadStorage && setupForm.codigoEntidad && setupForm.codigoEntidad.trim().length >= 3) {
            fetchUbicaciones(setupForm.codigoEntidad.trim());
        }
    }, [entidadStorage, setupForm.codigoEntidad, fetchUbicaciones]);

    const handleSetupEntidad = (e) => {
        const val = e.target.value;
        setUbicaciones([]);
        setEntidadSetup(null);
        setSetupForm(p => ({ ...p, codigoEntidad: val, codigoUbicacion: '' }));
    };

    const handleIngresar = async () => {
        if (!setupForm.codigoEntidad || !setupForm.codigoUbicacion)
            return sweet('Atención', 'Faltan datos de entidad o sede');
        await serverApi.post('/auth/registro-pantallas', setupForm);
        localStorage.setItem('codigoEntidad', setupForm.codigoEntidad);
        localStorage.setItem('codigoUbicacion', setupForm.codigoUbicacion);
        localStorage.setItem('nombreEntidad', entidadSetup?.nombreEntidad || '');
        localStorage.setItem('imagenLogo', entidadSetup?.imagenLogo || '');
        localStorage.setItem('imagenIcono', entidadSetup?.imagenIcono || '');
        guardarColoresStorage(entidadSetup?.colorPrimario, entidadSetup?.colorSecundario);
        conectarSocket({
            query: {
                codigoEntidad: setupForm.codigoEntidad,
                ubicacion: setupForm.codigoUbicacion,
            },
        });
        window.location.reload();
    };

    /* ─── TECLADO NUMÉRICO ─── */
    const handleNumKey = (key) => {
        if (key === 'DEL') {
            setNumDoc(p => p.slice(0, -1));
        } else {
            setNumDoc(p => p.length >= 12 ? p : p + String(key));
        }
        // Al escribir a mano se descarta el estado de la última lectura
        setLecturaCedula(null);
    };

    /* ─── TECLADO QWERTY ─── */
    const handleQwertyKey = useCallback((char) => {
        setDatosPaciente(p => ({ ...p, [campoActivo]: p[campoActivo] + char }));
    }, [campoActivo]);

    const handleQwertyDelete = useCallback(() => {
        setDatosPaciente(p => ({ ...p, [campoActivo]: p[campoActivo].slice(0, -1) }));
    }, [campoActivo]);

    const handleQwertySpace = useCallback(() => {
        setDatosPaciente(p => ({ ...p, [campoActivo]: p[campoActivo] + ' ' }));
    }, [campoActivo]);

    /* ─── PASO 1 → Buscar paciente ─── */
    const buscarPorDocumento = async (documento, tipo, nombresLeidos = null) => {
        const doc = String(documento ?? '').trim();
        const tipoFinal = tipo || tipoDoc;

        if (!tipoFinal || !doc) return sweet('Atención', 'Complete el tipo y número de documento');

        // Sin nombres aportados por la pistola arrancamos el formulario limpio
        if (!nombresLeidos) {
            setDatosPaciente({ nombre1: '', nombre2: '', apellido1: '', apellido2: '' });
        }

        try {
            setIsLoading(true);
            const resp = await serverApi.post('/tickets/busca-paciente', {
                codigoEntidad: localStorage.getItem('codigoEntidad'),
                numeroDocumento: doc,
            });

            const lista = resp.data.paciente || [];

            if (lista.length === 0) {
                setPacienteNombre('');
                setPacienteEsNuevo(true);
                setPaso('1b');
                // Si la pistola ya aportó los nombres, el paciente sólo
                // confirma; el cursor queda en el primer campo vacío
                const campos = ['nombre1', 'nombre2', 'apellido1', 'apellido2'];
                const datos = nombresLeidos || {};
                const vacio = campos.find((k) => !String(datos[k] || '').trim());
                setCampoActivo(vacio || 'nombre1');
                setCapsOn(true);
            } else {
                setPacienteNombre(lista[0].paciente || '');
                setPacienteEsNuevo(false);
                setPaso(2);
            }
        } catch (e) {
            console.error(e);
            sweet('Error', 'No se pudo consultar el paciente');
        } finally {
            setIsLoading(false);
        }
    };

    // Botón CONTINUAR del paso 1 (usa lo que esté en pantalla)
    const buscarPaciente = () => buscarPorDocumento(numDoc, tipoDoc);

    /* ─── LECTURA CON PISTOLA (scanner de cédulas) ───
       Soporta los varios formatos de las cédulas colombianas; el
       reconocimiento está en src/helpers/lectorCedula.js */
    const ultimaLectura = useRef({ crudo: '', t: 0 });

    const aplicarLecturaCedula = (crudo) => {
        const ahora = Date.now();

        // Algunas pistolas emiten la misma lectura dos veces seguidas
        if (crudo === ultimaLectura.current.crudo && ahora - ultimaLectura.current.t < 1500) return;
        ultimaLectura.current = { crudo, t: ahora };

        const lectura = parsearLecturaCedula(crudo);
        setLecturaCedula(lectura);

        if (!lectura.ok) return;

        const nombresLeidos = {
            nombre1: lectura.nombre1 || '',
            nombre2: lectura.nombre2 || '',
            apellido1: lectura.apellido1 || '',
            apellido2: lectura.apellido2 || '',
        };
        const tieneNombres = Object.values(nombresLeidos).some(Boolean);

        setNumDoc(lectura.numeroDocumento);
        if (lectura.tipoDocumento) setTipoDoc(lectura.tipoDocumento);
        if (tieneNombres) setDatosPaciente(nombresLeidos);

        // La lectura dispara la búsqueda del paciente (pasos 1 y 1b)
        buscarPorDocumento(
            lectura.numeroDocumento,
            lectura.tipoDocumento,
            tieneNombres ? nombresLeidos : null
        );
    };

    useLectorCedula({
        // Sólo cuando el kiosko ya está configurado y no se está eligiendo
        // el tipo de atención
        activo: Boolean(entidadStorage) && paso !== 2,
        onLectura: aplicarLecturaCedula,
    });

    /* ─── PASO 1b → Confirmar datos nuevos ─── */
    const confirmarDatosNuevo = () => {
        const nombreCompleto = [datosPaciente.nombre1, datosPaciente.nombre2, datosPaciente.apellido1, datosPaciente.apellido2]
            .filter(Boolean).join(' ').toUpperCase();
        setPacienteNombre(nombreCompleto);
        setPaso(2);
    };

    /* ─── PASO 2 → Crear ticket ─── */
    const crearTicket = async (codigoTipoAtencion, uidTipoAtencion) => {
        const codigoEntidad = localStorage.getItem('codigoEntidad');
        const codigoUbicacion = localStorage.getItem('codigoUbicacion');

        try {
            setIsLoading(true);

            if (pacienteEsNuevo) {
                await serverApi.post('/tickets/registra-cliente', {
                    codigoEntidad,
                    tipoDocumento: tipoDoc,
                    numeroDocumento: numDoc,
                    ...datosPaciente,
                });
            }

            const resp = await serverApi.post('/tickets/crear-ticket', {
                codigoEntidad,
                codigoUbicacion,
                uidTipoAtencion,
                indPrioridad: prioridad ? 1 : 0,
                tipoDocumento: tipoDoc,
                numeroDocumento: numDoc,
            });

            const result = resp.data.ticket;

            sweet('', `
                <div style="display:flex;flex-direction:column;align-items:center;padding:8px">
                    ${iconSvgOk}
                    <br/>
                    <h2 style="margin:8px 0;font-weight:900">Turno: ${codigoTipoAtencion}-${result.numero}</h2>
                    <p style="color:#475569">Por favor, esté atento al televisor o al llamado de voz para saber en qué módulo será atendido.</p>
                    <b style="font-size:18px;margin-top:8px">¡Gracias!</b>
                </div>
            `);

            // Imprimir ticket (logo de la entidad configurado)
            const win = window.open('', 'PRINT', 'height=400,width=300');
            win.document.write(`
                <html><head><title>Ticket</title>
                <style>
                    body{font-family:Arial;text-align:center;width:58mm;margin:0;padding:10px;}
                    h1,h4,h5,h6{margin:4px 0;}img{width:120px;}
                </style></head>
                <body>
                    <img src="${logoPrint}"/>
                    <h1>Turno: ${codigoTipoAtencion}-${result.numero}</h1>
                    <h4>Paciente: ${numDoc}</h4>
                    ${pacienteNombre ? `<h5>${pacienteNombre}</h5>` : ''}
                    <h5>${result.fecha}</h5>
                    <h6>-- PITSoluciones.com --</h6>
                </body></html>
            `);
            win.document.close();
            win.focus();
            win.print();
            setTimeout(() => win.close(), 4000);

            // Reset
            setNumDoc('');
            setTipoDoc('CC');
            setPrioridad(false);
            setPacienteNombre('');
            setPacienteEsNuevo(false);
            setDatosPaciente({ nombre1: '', nombre2: '', apellido1: '', apellido2: '' });
            setCampoActivo('nombre1');
            setLecturaCedula(null);
            setPaso(1);

        } catch (e) {
            console.error(e);
            sweet('Error', 'No se pudo crear el ticket');
        } finally {
            setIsLoading(false);
        }
    };

    /* ═══════════════════════════════════════════
       RENDER: Setup (sin entidad configurada)
    ═══════════════════════════════════════════ */
    if (!entidadStorage) {
        // Tarjeta ancha: se prefiere el logo; el ícono queda como respaldo
        const urlLogoSetup = fullImageUrl(entidadSetup?.imagenLogo);
        const urlIconoSetup = fullImageUrl(entidadSetup?.imagenIcono);
        const logoSetup = urlLogoSetup || urlIconoSetup || logo;
        return (
            <div style={{
                minHeight: '100vh',
                background: '#f4f7f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 20,
            }}>
                <div style={{
                    background: 'rgba(255,255,255,0.97)',
                    borderRadius: 24,
                    padding: '36px 32px',
                    width: '100%',
                    maxWidth: 460,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.15)',
                }}>
                    <div style={{ textAlign: 'center', marginBottom: 28 }}>
                        <LogoKiosko src={logoSetup} fallback={urlIconoSetup} alt="logo" style={{ height: 64, maxWidth: '100%', objectFit: 'contain', marginBottom: 16 }} />
                        <h3 style={{ color: 'var(--color-primario-texto)', fontWeight: 900, margin: 0, fontSize: 22 }}>Configurar Kiosko</h3>
                        <p style={{ color: '#64748b', marginTop: 6, fontSize: 14 }}>
                            {entidadSetup?.nombreEntidad || 'Ingrese su entidad y sede de atención'}
                        </p>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: 1, textTransform: 'uppercase' }}>
                            Código Entidad
                        </label>
                        <input
                            type="text"
                            value={setupForm.codigoEntidad}
                            onChange={handleSetupEntidad}
                            placeholder="Ej: femcc-01"
                            style={{
                                width: '100%', padding: '14px', borderRadius: 12,
                                border: '2px solid #e2e8f0', fontSize: 16, marginTop: 6,
                                outline: 'none', boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    {ubicaciones.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: 1, textTransform: 'uppercase' }}>
                                Sede / Ubicación
                            </label>
                            <div style={{ marginTop: 6 }}>
                                <SelectBuscable
                                    options={ubicaciones}
                                    value={setupForm.codigoUbicacion}
                                    onChange={(value) => setSetupForm(p => ({ ...p, codigoUbicacion: value }))}
                                    placeholder="Seleccione..."
                                    isClearable={false}
                                    fontSize="15px"
                                    height={48}
                                />
                            </div>
                        </div>
                    )}

                    {setupForm.codigoUbicacion && (
                        <button
                            onClick={handleIngresar}
                            style={{
                                width: '100%', padding: '16px', borderRadius: 14,
                                border: 'none', background: 'linear-gradient(135deg, var(--color-primario), var(--color-secundario))',
                                color: 'var(--color-marca-contraste)', fontSize: 16, fontWeight: 800,
                                cursor: 'pointer', boxShadow: '0 4px 16px rgba(var(--color-primario-rgb),0.4)',
                            }}
                        >
                            INGRESAR AL KIOSKO →
                        </button>
                    )}
                </div>
            </div>
        );
    }

    /* ═══════════════════════════════════════════
       ESTILOS GLOBALES Y MEDIA QUERIES RESPONSIVAS
    ═══════════════════════════════════════════ */
    const globalCss = `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes slideUp {
            from { opacity:0; transform: translateY(16px); }
            to   { opacity:1; transform: translateY(0); }
        }
        .ct2-card { animation: slideUp 0.3s ease; }
        .ct2-root * { font-family: 'Inter', Arial, sans-serif; }
        /* Proteger Font Awesome del override anterior (los iconos usan su propia font) */
        .ct2-root .fa-solid, .ct2-root .fas, .ct2-root .fa-regular, .ct2-root .far {
            font-family: "Font Awesome 6 Free" !important;
        }
        .ct2-root .fa-brands, .ct2-root .fab {
            font-family: "Font Awesome 6 Brands" !important;
        }

        /* Grid Responsiva Principal para Paso 1 */
        .ct2-step1-grid {
            display: grid;
            grid-template-columns: 1.1fr 0.9fr;
            gap: 20px;
            align-items: start;
        }

        /* Selector de Tipo de Doc */
        .ct2-selector-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
            margin-bottom: 24px;
        }

        /* Rejilla Formulario Nuevo Paciente Paso 1b */
        .ct2-form-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 8px;
        }

        /* Rejilla de Tipos de Atención Paso 2 */
        .ct2-atencion-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            grid-auto-rows: minmax(min-content, 1fr);
            gap: 16px;
            flex: 1;
            margin-top: 0;
            min-height: 0;
            /* Si hay muchos tipos de atención, hace scroll en vez de recortarlos */
            overflow-y: auto;
            padding-right: 4px;
        }

        /* Kiosko: la pantalla ocupa el alto del monitor sin scroll externo */
        .main-content:has(.ct2-root) {
            height: 100vh;
            height: 100dvh;
            overflow: hidden;
        }
        .page-container:has(.ct2-root) {
            padding: 0 !important;
            overflow: hidden;
        }

        /* Pantalla Paso 2 en dos columnas */
        .ct2-step2-grid {
            display: grid;
            grid-template-columns: 280px 1fr;
            gap: 16px;
            align-items: stretch;
        }

        /* Media Queries para Tablets y Pantallas Medianas */
        @media (max-width: 900px) {
            .ct2-step1-grid {
                grid-template-columns: 1fr;
            }
            .ct2-selector-grid {
                grid-template-columns: repeat(3, 1fr);
            }
            .ct2-atencion-grid {
                grid-template-columns: 1fr;
            }
            .ct2-step2-grid {
                grid-template-columns: 1fr;
            }
        }

        /* Media Queries para Teléfonos Celulares */
        @media (max-width: 600px) {
            .ct2-selector-grid {
                grid-template-columns: repeat(2, 1fr);
                gap: 8px;
            }
            .ct2-form-grid {
                grid-template-columns: 1fr;
            }
            .ct2-atencion-grid {
                grid-template-columns: 1fr;
            }
        }

        /* ── Seguridad de alto (kioskos con poca altura útil) ──
           Cada paso vive dentro de .ct2-step: si el contenido cabe, se centra
           verticalmente; si no cabe, hace scroll interno en lugar de desbordar
           hacia arriba y tapar el encabezado (marca / número de documento). */
        .ct2-step {
            width: 100%;
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            overflow-y: auto;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: thin;
        }
        .ct2-step-inner {
            width: 100%;
            margin-top: auto;
            margin-bottom: auto;
        }

        /* ── Marca en el encabezado / panel lateral ──
           El bloque de texto no debe empujar la etiqueta: el nombre se recorta
           con puntos suspensivos (igual que .navbar-marca-nombre) y el logo
           tiene un ancho máximo. En pantallas chicas se achican ambos. */
        .ct2-brand-text {
            min-width: 0;
            flex: 1 1 auto;
            overflow: hidden;
        }
        .ct2-brand-name {
            display: block;
            margin: 0;
            font-weight: 800;
            font-size: 16px;
            line-height: 1.2;
            color: #0f172a;
            max-width: clamp(160px, 30vw, 420px);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        .ct2-brand-name--panel {
            max-width: 100%;
            font-size: 17px;
            line-height: 1.25;
            margin: 12px 0 2px;
        }
        .ct2-brand-logo {
            height: 46px;
            max-width: 220px;
            object-fit: contain;
            flex-shrink: 0;
        }
        .ct2-brand-logo--panel {
            height: 56px;
            max-width: 100%;
        }

        @media (max-width: 768px) {
            .ct2-brand-name { font-size: 14px; }
            .ct2-brand-logo { height: 34px; max-width: 130px; }
            .ct2-brand-logo--panel { height: 48px; }
            /* El logo va primero y nada salta de línea: así no queda debajo
               del nombre en pantallas de celular */
            .ct2-brand-header {
                flex-wrap: nowrap !important;
                gap: 10px;
                padding: 10px 14px !important;
            }
            /* La sede es secundaria: se oculta si no entra */
            .ct2-brand-header .ct2-brand-right span {
                font-size: 10.5px !important;
                padding: 4px 8px !important;
            }
        }

        @media (max-width: 480px) {
            /* En pantallas muy chicas la sede estorba: se oculta y queda
               logo + nombre de la empresa */
            .ct2-brand-header .ct2-brand-right { display: none; }
            .ct2-brand-logo { max-width: 96px; }
        }

        /* Teclado numérico: se achica en pantallas bajas para que todo el
           paso 1 entre sin scroll */
        .ct2-num-key { height: 68px; }
        .ct2-doc-display { padding: 12px 24px; }
        .ct2-doc-num {
            font-size: clamp(26px, 4.6vh, 38px);
            min-height: 46px;
        }
        .ct2-doc-num--vacio {
            font-size: clamp(18px, 3.2vh, 26px);
        }

        @media (max-height: 820px) {
            .ct2-num-key { height: 56px; }
            .ct2-doc-display { padding: 8px 24px; }
            .ct2-doc-num { min-height: 38px; }
        }
        @media (max-height: 700px) {
            .ct2-num-key { height: 46px; }
            .ct2-doc-display { padding: 6px 20px; }
            .ct2-doc-num { min-height: 32px; }
        }
    `;

    /* ─── Layout styles ─── */
    const bg = '#f4f7f6';

    const panelStyle = {
        background: 'white',
        borderRadius: 22,
        padding: '22px 24px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
        width: '100%',
        boxSizing: 'border-box',
    };

    const headerLabelStyle = {
        fontSize: 12,
        fontWeight: 800,
        color: 'var(--color-primario-texto)',
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        marginBottom: 10,
    };

    return (
        <>
            <style>{globalCss}</style>
            {isLoading && <LoadingOverlay />}

            <div
                className="ct2-root"
                style={{
                    height: '100vh',
                    height: '100dvh',
                    background: bg,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '12px 16px 16px',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                }}
            >
                {/* ── HEADER: branding de la entidad (pasos 1 y 1b) ── */}
                {paso !== 2 && (
                <div style={{ width: '100%', maxWidth: 1200, marginBottom: 14 }}>
                    <div
                        className="ct2-brand-header"
                        style={{
                            background: '#ffffff', borderRadius: 18, padding: '12px 22px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: 12, flexWrap: 'wrap',
                        }}
                    >
                        <LogoKiosko
                            src={logoMarca}
                            fallback={logoMarcaRespaldo}
                            alt={pantallaChica ? 'Ícono empresa' : 'Logo empresa'}
                            className="ct2-brand-logo"
                        />

                        <div
                            className="ct2-brand-text"
                            style={{ minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}
                        >
                            <h4
                                className="ct2-brand-name"
                                title={branding?.nombreEntidad || 'Ticket de Atención'}
                            >
                                {branding?.nombreEntidad || 'Ticket de Atención'}
                            </h4>
                            <p style={{ margin: '2px 0 0', fontSize: 11.5, color: '#64748b', fontWeight: 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                Emisión de turnos · Kiosko de autoatención
                            </p>
                        </div>

                        <div className="ct2-brand-right" style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, marginLeft: 'auto' }}>
                            {branding?.codigoUbicacion && (
                                <span style={{
                                    background: '#ecfdf5', color: '#047857', fontWeight: 800,
                                    padding: '5px 12px', borderRadius: 10, fontSize: 12,
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    whiteSpace: 'nowrap',
                                }}>
                                    <i className="fa-solid fa-location-dot"></i>
                                    {String(branding.codigoUbicacion).toUpperCase()}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                )}

                {/* ─────────────────────────────────────────
                    PASO 1: Ingreso de documento
                ───────────────────────────────────────── */}
                {paso === 1 && (
                    <div className="ct2-card ct2-paso1" style={{
                        width: '100%', maxWidth: 1100, flex: 1, minHeight: 0,
                        display: 'flex', flexDirection: 'column',
                    }}>

                        {/* Display número: estático (siempre visible mientras se escribe) */}
                        <div
                            className="ct2-doc-display"
                            style={{
                                background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-primario-oscuro) 100%)',
                                borderRadius: 22,
                                marginBottom: 16,
                                textAlign: 'center',
                                boxShadow: '0 10px 28px rgba(var(--color-primario-rgb),0.22)',
                                flexShrink: 0,
                            }}
                        >
                            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 6 }}>
                                Número de Documento
                            </div>
                            <div
                                className={`ct2-doc-num ${numDoc ? '' : 'ct2-doc-num--vacio'}`}
                                style={{
                                    fontWeight: 900,
                                    color: numDoc ? 'white' : 'rgba(255,255,255,0.4)',
                                    letterSpacing: 5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {numDoc || '···'}
                                {numDoc && (
                                    <span style={{
                                        display: 'inline-block', width: 3, height: 42,
                                        background: 'var(--color-secundario)', marginLeft: 8, borderRadius: 2,
                                        animation: 'blink 1s step-end infinite',
                                    }} />
                                )}
                            </div>
                        </div>

                        {/* Aviso y estado del lector de cédulas (pistola) */}
                        <div
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                gap: 10, marginBottom: 14, minHeight: 34, flexWrap: 'wrap',
                            }}
                        >
                            {lecturaCedula && lecturaCedula.ok ? (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0',
                                    padding: '7px 14px', borderRadius: 12, fontWeight: 700, fontSize: 13.5,
                                }}>
                                    <i className="fa-solid fa-circle-check"></i>
                                    Cédula leída · {describirFormato(lecturaCedula.formato)}
                                </span>
                            ) : lecturaCedula && lecturaCedula.aviso ? (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    background: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d',
                                    padding: '7px 14px', borderRadius: 12, fontWeight: 700, fontSize: 13,
                                }}>
                                    <i className="fa-solid fa-triangle-exclamation"></i>
                                    {lecturaCedula.aviso}
                                </span>
                            ) : (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    color: '#64748b', fontSize: 13.5, fontWeight: 600,
                                }}>
                                    <i className="fa-solid fa-id-card"></i>
                                    Acerca la cédula al lector, o digítala con el teclado
                                </span>
                            )}
                        </div>

                        {/* Zona desplazable: tipo de documento + teclado. El display
                            del documento queda fuera (estático) para que siempre se
                            vea mientras se está escribiendo */}
                        <div className="ct2-step">
                        <div className="ct2-step-inner">
                        <div className="ct2-step1-grid">
                            {/* Selector tipo doc + botón */}
                            <div style={panelStyle}>
                                <div style={headerLabelStyle}>Tipo de Documento</div>
                                <SelectorTipoDoc value={tipoDoc} onChange={setTipoDoc} />

                                <button
                                    onClick={buscarPaciente}
                                    disabled={!numDoc || numDoc.length < 4}
                                    style={{
                                        width: '100%',
                                        padding: '18px',
                                        borderRadius: 16,
                                        border: 'none',
                                        background: (!numDoc || numDoc.length < 4)
                                            ? '#e2e8f0'
                                            : 'linear-gradient(135deg,#16a34a,#22c55e)',
                                        color: (!numDoc || numDoc.length < 4) ? '#94a3b8' : 'white',
                                        fontSize: 18,
                                        fontWeight: 800,
                                        cursor: (!numDoc || numDoc.length < 4) ? 'not-allowed' : 'pointer',
                                        boxShadow: (!numDoc || numDoc.length < 4)
                                            ? 'none'
                                            : '0 6px 20px rgba(22,163,74,0.35)',
                                        transition: 'all 0.2s ease',
                                        letterSpacing: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 12,
                                        touchAction: 'manipulation',
                                        marginTop: 10,
                                    }}
                                >
                                    <span style={{ fontSize: 22 }}>→</span> CONTINUAR
                                </button>
                            </div>

                            {/* Teclado numérico */}
                            <div style={panelStyle}>
                                <div style={headerLabelStyle}>Teclado Numérico</div>
                                <TecladoNumerico onKey={handleNumKey} />
                            </div>
                        </div>
                        </div>
                        </div>
                    </div>
                )}

                {/* ─────────────────────────────────────────
                    PASO 1b: Datos del nuevo paciente
                ───────────────────────────────────────── */}
                {paso === '1b' && (
                    <div className="ct2-card ct2-step">
                    <div className="ct2-step-inner" style={{ maxWidth: 950 }}>

                        {/* Barra info */}
                        <div style={{
                            background: '#fffbeb',
                            border: '1.5px solid #fcd34d',
                            borderRadius: 16,
                            padding: '14px 20px',
                            marginBottom: 16,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 14,
                        }}>
                            <span style={{ fontSize: 28 }}>⚠️</span>
                            <div>
                                <div style={{ color: '#92400e', fontWeight: 800, fontSize: 16 }}>
                                    Paciente no encontrado
                                </div>
                                <div style={{ color: '#b45309', fontSize: 13 }}>
                                    Doc: <b>{tipoDoc}</b> — <b>{numDoc}</b> · Por favor ingrese sus datos para continuar
                                </div>
                            </div>
                        </div>

                        {/* Datos adicionales que aportó la cédula (informativos) */}
                        {lecturaCedula && lecturaCedula.ok
                            && (lecturaCedula.fechaNacimiento || lecturaCedula.sexo || lecturaCedula.rh) && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                                background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 16,
                                padding: '10px 18px', marginBottom: 16, color: '#047857',
                                fontSize: 13.5, fontWeight: 600,
                            }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 800 }}>
                                    <i className="fa-solid fa-id-card"></i> Leído de la cédula
                                </span>
                                {lecturaCedula.fechaNacimiento && (
                                    <span>Nacimiento: <b>{lecturaCedula.fechaNacimiento}</b></span>
                                )}
                                {lecturaCedula.sexo && (
                                    <span>Sexo: <b>{lecturaCedula.sexo === 'F' ? 'Femenino' : 'Masculino'}</b></span>
                                )}
                                {lecturaCedula.rh && <span>RH: <b>{lecturaCedula.rh}</b></span>}
                            </div>
                        )}

                        {/* Panel superior: campos + botones */}
                        <div style={{ ...panelStyle, marginBottom: 16 }}>
                            <div className="ct2-form-grid">
                                <CampoTextoTouch
                                    label="Primer Nombre *"
                                    value={datosPaciente.nombre1}
                                    active={campoActivo === 'nombre1'}
                                    onActivate={() => setCampoActivo('nombre1')}
                                />
                                <CampoTextoTouch
                                    label="Segundo Nombre"
                                    value={datosPaciente.nombre2}
                                    active={campoActivo === 'nombre2'}
                                    onActivate={() => setCampoActivo('nombre2')}
                                />
                                <CampoTextoTouch
                                    label="Primer Apellido *"
                                    value={datosPaciente.apellido1}
                                    active={campoActivo === 'apellido1'}
                                    onActivate={() => setCampoActivo('apellido1')}
                                />
                                <CampoTextoTouch
                                    label="Segundo Apellido"
                                    value={datosPaciente.apellido2}
                                    active={campoActivo === 'apellido2'}
                                    onActivate={() => setCampoActivo('apellido2')}
                                />
                            </div>

                            {/* Tabs de campos */}
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6, marginTop: 6 }}>
                                {[
                                    { key: 'nombre1', label: '1er Nombre' },
                                    { key: 'nombre2', label: '2do Nombre' },
                                    { key: 'apellido1', label: '1er Apellido' },
                                    { key: 'apellido2', label: '2do Apellido' },
                                ].map(f => (
                                    <button
                                        key={f.key}
                                        onClick={() => setCampoActivo(f.key)}
                                        style={{
                                            padding: '8px 16px',
                                            borderRadius: 20,
                                            border: 'none',
                                            background: campoActivo === f.key ? 'var(--color-primario)' : '#e2e8f0',
                                            color: campoActivo === f.key ? 'white' : '#475569',
                                            fontSize: 13,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            touchAction: 'manipulation',
                                        }}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Teclado QWERTY */}
                        <div style={{ ...panelStyle, marginBottom: 16 }}>
                            <TecladoQwerty
                                onKey={handleQwertyKey}
                                onDelete={handleQwertyDelete}
                                onSpace={handleQwertySpace}
                                onCaps={() => setCapsOn(p => !p)}
                                capsOn={capsOn}
                            />
                        </div>

                        {/* Botones acción */}
                        <div style={{ display: 'flex', gap: 14 }}>
                            <button
                                onClick={() => { setPaso(1); }}
                                style={{
                                    flex: 1, padding: '18px', borderRadius: 16,
                                    border: '2px solid #cbd5e1',
                                    background: '#ffffff',
                                    color: '#475569', fontSize: 16, fontWeight: 700,
                                    cursor: 'pointer', touchAction: 'manipulation',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                }}
                            >
                                ← Atrás
                            </button>
                            <button
                                onClick={confirmarDatosNuevo}
                                style={{
                                    flex: 2, padding: '18px', borderRadius: 16,
                                    border: 'none',
                                    background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                                    color: 'white', fontSize: 17, fontWeight: 800,
                                    cursor: 'pointer', touchAction: 'manipulation',
                                    boxShadow: '0 4px 16px rgba(22,163,74,0.35)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                }}
                            >
                                ✓ Confirmar Datos
                            </button>
                        </div>
                    </div>
                    </div>
                )}

                {/* ─────────────────────────────────────────
                    PASO 2: Selección de tipo de atención
                ───────────────────────────────────────── */}
                {paso === 2 && (
                    <div className="ct2-card ct2-step2-grid" style={{
                        width: '100%', maxWidth: 1440, flex: 1, minHeight: 0,
                    }}>

                        {/* ── Columna lateral: branding + paciente ── */}
                        <div style={{
                            ...panelStyle,
                            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 18,
                            minHeight: 0,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                        }}>
                            {/* Marca en el recuadro del panel lateral: logo en
                                pantalla grande, ícono en pantalla chica */}
                            <div style={{ textAlign: 'center', paddingTop: 4 }}>
                                <div style={{
                                    background: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 18,
                                    padding: '10px 18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    maxWidth: '100%',
                                    minHeight: 80,
                                    boxSizing: 'border-box',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                    overflow: 'hidden',
                                }}>
                                    <LogoKiosko
                                        src={logoMarca}
                                        fallback={logoMarcaRespaldo}
                                        alt={pantallaChica ? 'Ícono empresa' : 'Logo empresa'}
                                        className="ct2-brand-logo ct2-brand-logo--panel"
                                    />
                                </div>
                                <h4
                                    className="ct2-brand-name ct2-brand-name--panel"
                                    title={branding?.nombreEntidad || 'Ticket de Atención'}
                                >
                                    {branding?.nombreEntidad || 'Ticket de Atención'}
                                </h4>
                                <p style={{ margin: '0 0 8px', fontSize: 11.5, color: '#64748b', fontWeight: 400 }}>
                                    Emisión de turnos · Kiosko de autoatención
                                </p>
                                {branding?.codigoUbicacion && (
                                    <span style={{
                                        background: '#ecfdf5', color: '#047857', fontWeight: 800,
                                        padding: '4px 12px', borderRadius: 50, fontSize: 12,
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        border: '1px solid #a7f3d0',
                                    }}>
                                        <i className="fa-solid fa-location-dot" style={{ fontSize: 11 }} />
                                        {String(branding.codigoUbicacion).toUpperCase()}
                                    </span>
                                )}
                            </div>

                            {/* Paciente */}
                            <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: 18 }}>
                                <div style={{ color: '#94a3b8', fontSize: 10.5, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
                                    Paciente
                                </div>
                                <div style={{
                                    background: 'var(--color-primario-suave)',
                                    border: '1.5px solid var(--color-primario-borde)',
                                    borderRadius: 16,
                                    padding: '12px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div style={{
                                            width: 46, height: 46, borderRadius: '50%',
                                            background: 'linear-gradient(135deg, var(--color-primario), var(--color-secundario))',
                                            color: 'var(--color-marca-contraste)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 22, flexShrink: 0,
                                        }}>
                                            👤
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ color: 'var(--color-primario-texto)', fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>
                                                {tiposDocumentoLabels[tipoDoc]}
                                                {pacienteEsNuevo && <span style={{
                                                    background: '#f59e0b', color: 'white',
                                                    fontSize: 9, fontWeight: 800, borderRadius: 6,
                                                    padding: '1px 7px', marginLeft: 6,
                                                }}>NUEVO</span>}
                                            </div>
                                            <div style={{ color: '#0f172a', fontSize: 16, fontWeight: 800, marginTop: 1, lineHeight: 1.25 }}>
                                                {pacienteNombre || 'Paciente'}
                                            </div>
                                            <div style={{ color: '#475569', fontSize: 12, fontWeight: 600, marginTop: 1 }}>
                                                N° {numDoc}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setPaso(1)}
                                        style={{
                                            background: '#ffffff', border: '1.5px solid var(--color-primario)',
                                            color: 'var(--color-primario-texto)', borderRadius: 11, padding: '10px 16px',
                                            fontSize: 13, fontWeight: 800, cursor: 'pointer',
                                            touchAction: 'manipulation', width: '100%',
                                            boxShadow: '0 2px 6px rgba(var(--color-primario-rgb),0.12)',
                                        }}
                                    >
                                        ← Cambiar paciente
                                    </button>
                                </div>
                            </div>

                            {/* Paso actual */}
                            <div style={{
                                background: '#f1f5f9', borderRadius: 14, padding: '12px 16px',
                                display: 'flex', alignItems: 'center', gap: 12, marginTop: 'auto',
                            }}>
                                <div style={{
                                    width: 34, height: 34, borderRadius: '50%',
                                    background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 15, flexShrink: 0,
                                }}>
                                    ✓
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                                        Paso 1 · Datos del paciente
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--color-primario-texto)', fontWeight: 800 }}>
                                        Paso 2 · Tipo de atención
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Columna principal: servicios ── */}
                        <div style={{
                            ...panelStyle,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                flexWrap: 'wrap', gap: 12,
                            }}>
                                <div style={headerLabelStyle}>¿Cuál es el motivo de su visita?</div>
                                <button
                                    onClick={() => setPrioridad(p => !p)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '11px 18px',
                                        borderRadius: 50,
                                        border: prioridad ? 'none' : '2px solid #ef4444',
                                        background: prioridad
                                            ? 'linear-gradient(135deg,#dc2626,#ef4444)'
                                            : '#ffffff',
                                        color: prioridad ? 'white' : '#ef4444',
                                        fontSize: 14,
                                        fontWeight: 800,
                                        cursor: 'pointer',
                                        touchAction: 'manipulation',
                                        boxShadow: prioridad ? '0 6px 20px rgba(220,38,38,0.4)' : '0 2px 8px rgba(0,0,0,0.06)',
                                        transition: 'all 0.2s ease',
                                        flexShrink: 0,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <i className="fa-solid fa-wheelchair" style={{ fontSize: 16 }} />
                                    {prioridad ? '✓ ATENCIÓN PRIORITARIA' : 'ATENCIÓN PRIORITARIA'}
                                </button>
                            </div>
                            <div style={{ marginTop: 14, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                {tipoAtencion.length === 0 && (
                                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40, fontSize: 16 }}>
                                        Sin tipos de atención configurados
                                    </div>
                                )}
                                <div className="ct2-atencion-grid">
                                    {tipoAtencion.map(item => (
                                        <CardTipoAtencion
                                            key={item.uidTipoAtencion}
                                            item={item}
                                            onClick={() => crearTicket(item.codigoTipoAtencion, item.uidTipoAtencion)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};